import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  getrecentCollectionSource,
  getrecentCollections,
} from "../services/recent-collection.service";

const useRecentCollection = (filter) => {
  const [recentCollections, setRecentCollections] = useState([]);
  const [filteredRecentCollection, setFilteredRecentCollection] = useState([]);
  const [recentCollectionSource, setRecentCollectionSource] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { user } = useSelector((state) => state.auth);
  const { isSidebarOpen } = useSelector((state) => state.data);
  const userId = user?.userId;
  const abortControllerRef = useRef(null);
  const didMountSidebarRef = useRef(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!userId) return;
      setIsLoading(true);
      try {
        const [collections, sources] = await Promise.all([
          getrecentCollections(user),
          getrecentCollectionSource(user, ""),
        ]);
        setRecentCollections(collections);
        setRecentCollectionSource(sources);
        setFilteredRecentCollection(sources);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, [userId]);

  useEffect(() => {
    abortControllerRef.current?.abort();
    if (!filter) {
      setFilteredRecentCollection(recentCollectionSource);
      if (recentCollectionSource.length > 0) {
        setIsLoading(false);
      }
      return;
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const getFilteredCollectionSource = async () => {
      if (!userId) return;
      setIsLoading(true);
      try {
        const data = await getrecentCollectionSource(user, filter, signal);
        if (!signal.aborted) {
          setFilteredRecentCollection(data);
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error(e);
        }
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    };
    getFilteredCollectionSource();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [filter, userId]);

  useEffect(() => {
    // The bookmark sidebar's delete action removes a bookmark from every
    // collection it belongs to, but this table's data is otherwise only
    // fetched once on mount, so the Collections column here goes stale
    // until the sidebar closes.
    if (!didMountSidebarRef.current) {
      didMountSidebarRef.current = true;
      return;
    }
    if (!userId || isSidebarOpen) return;
    const refreshAfterSidebarClose = async () => {
      try {
        const [collections, sources] = await Promise.all([
          getrecentCollections(user),
          getrecentCollectionSource(user, filter || ""),
        ]);
        setRecentCollections(collections);
        if (!filter) {
          setRecentCollectionSource(sources);
        }
        setFilteredRecentCollection(sources);
      } catch (e) {
        console.error(e);
      }
    };
    refreshAfterSidebarClose();
  }, [isSidebarOpen]);

  return {
    recentCollections,
    recentCollectionSource,
    filteredRecentCollection,
    isLoading,
  };
};

export default useRecentCollection;
